import React, { FunctionComponent } from 'react'

import { useReactiveVar } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { appSectionVar } from '@/apollo/cache'
import AiSparkIcon from '@/atoms/icons/ai_spark.svg'
import AnnualReviewIcon from '@/atoms/icons/annual-review.svg'
import BankIcon from '@/atoms/icons/bank.svg'
import BulkExternalVerificationTasksIcon from '@/atoms/icons/bulk.svg'
import ClientsIcon from '@/atoms/icons/client.svg'
import ClientRequirementsIcon from '@/atoms/icons/client-requirements.svg'
import CreditLineIcon from '@/atoms/icons/credit-line.svg'
import DebtCollectionIcon from '@/atoms/icons/debt-collection.svg'
import DebtorsIcon from '@/atoms/icons/debtors.svg'
import HomeIcon from '@/atoms/icons/home.svg'
import InvestorsIcon from '@/atoms/icons/investors.svg'
import InvoicesIcon from '@/atoms/icons/invoices.svg'
import LedgerIcon from '@/atoms/icons/ledger.svg'
import LimitIncreaseIcon from '@/atoms/icons/limit.svg'
import LoansIcon from '@/atoms/icons/loans.svg'
import NotesIcon from '@/atoms/icons/note.svg'
import OffersIcon from '@/atoms/icons/offers.svg'
import OutstandingVerificationTasksIcon from '@/atoms/icons/outstanding.svg'
import PaymentsReportsIcon from '@/atoms/icons/payments-reports.svg'
import PigIcon from '@/atoms/icons/pig.svg'
import ProspectListIcon from '@/atoms/icons/prospect.svg'
import ReportingIcon from '@/atoms/icons/reporting.svg'
import ReportsIcon from '@/atoms/icons/reports.svg'
import ReverseFactoringIcon from '@/atoms/icons/reverse-factoring.svg'
import SettlementsIcon from '@/atoms/icons/settlement.svg'
import StopListIcon from '@/atoms/icons/stop-person.svg'
import SystemIcon from '@/atoms/icons/system.svg'
import TaskManagerIcon from '@/atoms/icons/task_manager.svg'
import UsersIcon from '@/atoms/icons/users.svg'
import { isProduction } from '@/helpers/config'
import { getRoleFromLocalStorage } from '@/helpers/storage-utils'
import { NavigationLink } from '@/layout/common/sidebar/navigation-link'
import { AppSections, ClientPortalClientPropertiesDTO, UserRole } from '@/models/auth'
import { AdministrationTypes } from '@/models/common'
import { clientPath, path } from '@/navigation'

interface Props {
  clientPortalProperties?: ClientPortalClientPropertiesDTO
}

const backOfficeLinks = (appSection: AppSections) => [
  {
    path: path.prospects,
    key: 'prospects',
    icon: ProspectListIcon,
    hide: appSection !== AppSections.RISK_MANAGEMENT,
  },
  {
    path: path.clients,
    key: 'clients',
    icon: ClientsIcon,
    hide: appSection !== AppSections.FACTORING,
  },
  {
    path: path.debtors,
    key: 'debtors',
    icon: DebtorsIcon,
    hide: appSection !== AppSections.FACTORING,
  },
  {
    path: path.invoices,
    key: 'invoices',
    icon: InvoicesIcon,
    hide: appSection !== AppSections.FACTORING,
  },
  {
    path: path.notes,
    key: 'notes',
    icon: NotesIcon,
    hide: appSection !== AppSections.FACTORING,
  },
  {
    path: path.debtCollection,
    key: 'debtCollection',
    icon: DebtCollectionIcon,
    hide: appSection !== AppSections.FACTORING,
  },
  {
    path: path.clientRequirements,
    key: 'clientRequirements',
    icon: ClientRequirementsIcon,
    hide: appSection !== AppSections.FACTORING,
  },
  {
    path: path.outstandingVerificationTasks,
    key: 'outstandingVerification',
    icon: OutstandingVerificationTasksIcon,
    hide: appSection !== AppSections.FACTORING,
  },
  {
    path: path.bulkVerificationTasks,
    key: 'bulkExternalVerification',
    icon: BulkExternalVerificationTasksIcon,
    hide: appSection !== AppSections.FACTORING,
  },
  {
    path: path.aiTasks,
    key: 'aiTasks',
    icon: AiSparkIcon,
    hide: appSection !== AppSections.FACTORING,
  },
  {
    path: path.reports,
    key: 'reports',
    icon: ReportsIcon,
    hide: appSection !== AppSections.RISK_MANAGEMENT,
  },
  {
    path: path.stopList,
    key: 'stopList',
    icon: StopListIcon,
    hide: appSection !== AppSections.RISK_MANAGEMENT,
  },
  {
    path: path.limitIncrease,
    key: 'limitIncrease',
    icon: LimitIncreaseIcon,
    hide: appSection !== AppSections.RISK_MANAGEMENT,
  },
  {
    path: path.annualReview,
    key: 'annualReview',
    icon: AnnualReviewIcon,
    hide: appSection !== AppSections.RISK_MANAGEMENT,
  },
  {
    path: path.riskMonitoringTasks,
    key: 'riskMonitoringTasks',
    icon: TaskManagerIcon,
    hide: appSection !== AppSections.RISK_MANAGEMENT || isProduction,
  },
  {
    path: path.system,
    key: 'system',
    icon: SystemIcon,
    hide: appSection !== AppSections.ADMINISTRATION,
  },
  {
    path: path.reporting,
    key: 'reporting',
    icon: ReportingIcon,
    hide: appSection !== AppSections.ADMINISTRATION,
  },
  {
    path: path.bankStatements,
    key: 'bankStatements',
    icon: BankIcon,
    hide: appSection !== AppSections.PAYMENTS,
  },
  {
    path: path.generalLedgers,
    key: 'generalLedgers',
    icon: LedgerIcon,
    hide: appSection !== AppSections.PAYMENTS,
  },
  {
    path: path.payouts,
    key: 'payouts',
    icon: NotesIcon,
    hide: appSection !== AppSections.PAYMENTS,
  },
  {
    path: path.paymentsReports,
    key: 'paymentsReports',
    icon: PaymentsReportsIcon,
    hide: appSection !== AppSections.PAYMENTS,
  },
  {
    path: path.loans,
    key: 'loans',
    icon: LoansIcon,
    hide: appSection !== AppSections.LOAN_BOOK,
  },
  {
    path: path.creditLines,
    key: 'creditLines',
    icon: CreditLineIcon,
    hide: appSection !== AppSections.LOAN_BOOK,
  },
  {
    path: path.reverseFactoring,
    key: 'reverseFactoring',
    icon: ReverseFactoringIcon,
    hide: appSection !== AppSections.LOAN_BOOK,
  },
  {
    path: path.loanBookNotes,
    key: 'loanBookNotes',
    icon: NotesIcon,
    hide: appSection !== AppSections.LOAN_BOOK,
  },
  {
    path: path.loanBookReports,
    key: 'loanBookReports',
    icon: PaymentsReportsIcon,
    hide: appSection !== AppSections.LOAN_BOOK,
  },
  {
    path: path.loanBookDebtCollection,
    key: 'loanBookDebtCollection',
    icon: DebtCollectionIcon,
    hide: appSection !== AppSections.LOAN_BOOK,
  },
  {
    path: path.investors,
    key: 'investors',
    icon: InvestorsIcon,
    hide: appSection !== AppSections.INVESTMENT,
  },
  {
    path: path.offers,
    key: 'offers',
    icon: OffersIcon,
    hide: appSection !== AppSections.INVESTMENT,
  },
  {
    path: path.investmentPlans,
    key: 'investmentPlans',
    icon: PigIcon,
    hide: appSection !== AppSections.INVESTMENT,
  },
  {
    path: path.investmentNotes,
    key: 'investmentNotes',
    icon: NotesIcon,
    hide: appSection !== AppSections.INVESTMENT,
  },
  {
    path: path.investmentTaskManager,
    key: 'investmentTaskManager',
    icon: TaskManagerIcon,
    hide: appSection !== AppSections.INVESTMENT,
  },
]

const clientLinks = (clientPortalProperties: ClientPortalClientPropertiesDTO) => [
  {
    path: clientPath.home,
    key: 'home',
    icon: HomeIcon,
  },
  {
    path: clientPath.debtors,
    key: 'debtors',
    icon: DebtorsIcon,
  },
  {
    path: clientPath.invoices,
    key: 'invoices',
    icon: InvoicesIcon,
  },
  {
    path: clientPath.loanBookPayouts,
    key: 'loanBookPayouts',
    icon: CreditLineIcon,
    hide: !clientPortalProperties?.hasLoanBookProducts,
  },
  {
    path: clientPath.settlements,
    key: 'settlements',
    icon: SettlementsIcon,
  },
  {
    path: clientPath.notes,
    key: 'notes',
    icon: NotesIcon,
  },
  {
    path: clientPath.reports,
    key: 'paymentsReports',
    icon: ReportingIcon,
    hide:
      clientPortalProperties?.sourcingAgentAdministrates !== AdministrationTypes.NL_LOCAL &&
      clientPortalProperties?.sourcingAgentAdministrates !== AdministrationTypes.BE_LOCAL,
  },
  {
    path: clientPath.users,
    key: 'users',
    icon: UsersIcon,
  },
]

export const NavigationMenu: FunctionComponent<Props> = ({ clientPortalProperties }) => {
  const { t } = useTranslation()
  const initiallySelectedAppSection = useReactiveVar(appSectionVar)
  const role = getRoleFromLocalStorage()

  const links =
    role === UserRole.CLIENT
      ? clientLinks(clientPortalProperties).filter(({ hide }) => !hide)
      : backOfficeLinks(initiallySelectedAppSection).filter(({ hide }) => !hide)

  return (
    <StyledNavigation>
      {links.map(({ path, key, icon }) => (
        <NavigationLink key={key} path={path} linkText={t(`nav.${key}`)} linkIcon={icon} />
      ))}
    </StyledNavigation>
  )
}

const StyledNavigation = styled.nav`
  padding: ${({ theme }) => theme.space.S8} 0;
`
